<template>
  <b-modal :active.sync="isModalActive" has-modal-card trap-focus>
    <b-loading
      :is-full-page="true"
      v-model="isDataLoading"
      :can-cancel="true"
    ></b-loading>
    <div class="modal-card">
      <header class="modal-card-head">
        <h1 class="modal-card-title">{{ modalTitle }}</h1>
      </header>
      <section
        class="modal-card-body scrollable-modal-body"
        v-if="isEditAllowed || isViewMode"
      >
        <form @submit.prevent="submit">
          <div class="columns no-margin-bottom">
            <div class="column">
              <b-field label="Customer">
                <div>
                  <multiselect
                    :value="customers.id"
                    v-model="form.customer"
                    :options="customers"
                    placeholder="Please select a customer *"
                    label="name"
                    track-by="name"
                    :searchable="true"
                    :disabled="isViewMode"
                    required
                  ></multiselect>
                </div>
              </b-field>
              <b-field label="Supplier">
                <div>
                  <multiselect
                    :value="suppliers.id"
                    v-model="form.supplier"
                    :options="suppliers"
                    placeholder="Please select a supplier"
                    label="name"
                    track-by="name"
                    :searchable="true"
                    :disabled="isViewMode"
                  ></multiselect>
                </div>
              </b-field>
              <b-field label="Expiry Date">
                <b-datepicker
                  name="expiry_date"
                  v-model="form.expiry_date"
                  icon="calendar-today"
                  trap-focus
                  :disabled="isViewMode"
                  required
                  :date-formatter="dateFormatter"
                >
                </b-datepicker>
                <p v-if="validationErrors.expiry_date" class="help is-danger">
                  {{ validationErrors.expiry_date }}
                </p>
              </b-field>
              <section>
                <b-field
                  label="Choose a document to attach (PDF, jpg, jpeg, png)*"
                ></b-field>
                <b-field required class="file is-primary">
                  <b-upload
                    :disabled="isViewMode"
                    v-model="form.document"
                    class="file-label"
                    :multiple="!isEditMode"
                    drag-drop
                  >
                    <span>
                      <section class="section">
                        <div class="content has-text-centered">
                          <p>
                            <b-icon icon="upload" size="is-large"> </b-icon>
                          </p>
                          <p>
                            Drop your certificate files here or click to upload
                          </p>
                        </div>
                      </section>
                    </span>
                  </b-upload>
                </b-field>
                <p v-if="validationErrors.files" class="help is-danger">
                  {{ validationErrors.files }}
                </p>
                <div class="tags">
                  <span
                    v-for="(file, index) in form.document"
                    :key="index"
                    class="tag is-primary"
                    :style="{
                      cursor: isViewMode || isEditMode ? 'pointer' : 'default'
                    }"
                    @click="
                      isViewMode || isEditMode ? downloadFileFromTag() : null
                    "
                    :title="isViewMode || isEditMode ? 'Click to download' : ''"
                  >
                    {{ file.name }}
                    <button
                      class="delete is-small"
                      type="button"
                      v-if="isEditMode"
                      @click="deleteFile(index)"
                    ></button>
                  </span>
                </div>
              </section>
            </div>
          </div>
        </form>
      </section>
      <footer
        class="modal-card-foot"
        style="justify-content: flex-end"
        v-if="isEditAllowed || isViewMode"
      >
        <b-button class="is-normal" @click="closeModal">
          Cancel
        </b-button>
        <b-button
          v-if="!isViewMode"
          type="is-primary"
          @click.prevent="isnewDataFileListActiveMethod"
        >
          Save
        </b-button>
        <b-button
          v-if="isViewMode && downloadCertificate && editCertificate"
          type="is-primary"
          @click.prevent="downloadCertificateFile"
        >
          Download
        </b-button>
      </footer>
    </div>
    <b-modal v-model="isnewDataFileListActive" has-modal-card>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title title-success">Confirmation</p>
        </header>
        <section class="modal-card-body">
          <div class="columns no-margin-bottom">
            <b-field required class="file is-primary">
              <div class="content has-text-centered">
                <span>
                  There is already a certificate for this customer. Would you
                  like to delete all former certificates and replace with your
                  new uploaded certificates?"
                </span>
              </div>
            </b-field>
          </div>
        </section>
        <footer class="modal-card-foot">
          <b-button
            label="Cancel"
            class="is-normal"
            @click="isnewDataFileListActive = false"
          />
          <b-button
            label="Apply"
            type="is-primary"
            @click="submit"
            v-if="isEditAllowed"
          />
        </footer>
      </div>
    </b-modal>
  </b-modal>
</template>

<script>
import CustomerProfileDataService from "../../services/CustomerProfileDataService";
import DataManagementService from "../../services/DataManagementImportService";
import DataManagementCertificatesService from "@/services/DataManagementCertificatesService";
import Multiselect from "vue-multiselect";
import { UserGroup } from "@/services/UserGroups";
import { mapGetters } from "vuex";

const MAX_TOTAL_FILE_SIZE = 20 * 1024 * 1024; // 20MB in bytes
export default {
  name: "CertificateUploadForm",
  components: { Multiselect },
  props: {
    suppliers: {
      type: Array,
      default: () => []
    },
    isActive: {
      type: Boolean,
      default: false
    },
    editCertificate: {
      type: Object,
      default: null
    },
    viewCertificate: {
      type: Object,
      default: null
    },
    downloadCertificate: {
      type: Function,
      default: null
    }
  },
  data() {
    return {
      isModalActive: false,
      isDataLoading: false,
      isnewDataFileListActive: false,
      validationErrors: {},
      showReplaceImportCheckbox: "Replace Existing Data",
      showAddMoreImportCheckbox: "Add To Existing Data",
      isReplacedTD: false,
      isAddedMoreTD: false,
      customers: [],
      form: this.getClearFormObject(),
      customerDataService: new CustomerProfileDataService(),
      DataManagementService: new DataManagementService(),
      DataManagementCertificatesService: new DataManagementCertificatesService(),
      showPeriod: false
    };
  },
  computed: {
    ...mapGetters(["userId", "userName", "userHasPermission", "userGroups"]),
    isEditAllowed() {
      if (
        this.userGroups.includes(UserGroup.EPR_ADMIN) ||
        this.userGroups.includes(UserGroup.CUSTOMER_ACCOUNT_MANAGER)
      ) {
        return true;
      } else {
        return false;
      }
    },
    isViewMode() {
      return !!this.viewCertificate;
    },
    isEditMode() {
      return !!this.editCertificate;
    },
    modalTitle() {
      if (this.isViewMode) {
        return "View Certificate";
      } else if (this.isEditMode) {
        return "Edit Certificate";
      } else {
        return "Add Certificate";
      }
    }
  },
  watch: {
    isActive(newValue) {
      this.isModalActive = newValue;
      if (newValue) {
        // Clear validation errors when modal opens
        this.validationErrors = {};
        // Populate form if editing or viewing a certificate
        if (this.editCertificate) {
          this.populateFormData(this.editCertificate);
        } else if (this.viewCertificate) {
          this.populateFormData(this.viewCertificate);
        } else {
          this.form = this.getClearFormObject();
        }
      }
    },
    editCertificate(newValue) {
      if (newValue && this.isModalActive) {
        this.populateFormData(newValue);
      }
    },
    viewCertificate(newValue) {
      if (newValue && this.isModalActive) {
        this.populateFormData(newValue);
      }
    },
    isModalActive(newValue) {
      if (!newValue) {
        this.$emit("close");
      }
    },
    "form.document": {
      handler(newFiles) {
        // Ensure form.document is always an array
        if (newFiles && !(newFiles instanceof Array)) {
          this.form.document = [newFiles];
        }
        this.validateTotalFileSize(this.form.document);
      },
      deep: true
    }
  },
  mounted() {
    this.fetchCustomerData();
  },
  methods: {
    getReplacedTDVal() {
      this.isReplacedTD = false;
    },
    getisAddedMoreTD() {
      this.isAddedMoreTD = false;
    },
    isnewDataFileListActiveMethod() {
      //TODO: need to add condition to check whether the existing certificate is present for the selected customer or not. If present then only show the pop up
      this.submit();
    },
    getClearFormObject() {
      return {
        customer: null,
        expiry_date: null,
        supplier: null,
        document: []
      };
    },
    populateFormData(certificateData) {
      if (!certificateData) return;
      // Get customer ID from various possible properties
      const customerId =
        certificateData.customer_id || certificateData.customer;

      // Get supplier ID from various possible properties
      const supplierId =
        certificateData.supplier_id || certificateData.supplier;

      // Find matching customer from customers array
      const matchedCustomer = this.customers.find(c => c.id === customerId);

      // Find matching supplier from suppliers array
      const matchedSupplier = this.suppliers.find(s => s.id === supplierId);

      // Parse expiry_date if it's a string
      let expiryDate = certificateData.expiry_date;
      if (typeof expiryDate === "string") {
        expiryDate = new Date(expiryDate);
      }

      // Populate document - single document using active_document_name
      let documentArray = [];
      if (certificateData.active_document_name) {
        documentArray = [{ name: certificateData.active_document_name }];
      }

      this.form = {
        customer: matchedCustomer || null,
        supplier: matchedSupplier || null,
        expiry_date: expiryDate,
        document: documentArray
      };
    },
    closeModal() {
      this.isModalActive = false;
    },
    dateFormatter(dt) {
      if (dt != null) {
        return dt.toLocaleDateString("fr-CA");
      }
      return dt;
    },
    fetchCustomerData() {
      this.isDataLoading = true;
      this.customerDataService.getCustomerListForImport().subscribe(
        response => {
          this.customers = response;
          this.isDataLoading = false;
        },
        e => {
          this.isDataLoading = false;
          console.error("Error fetching customers:", e);
          this.$buefy.toast.open({
            message: "Failed to load customers",
            type: "is-danger"
          });
        }
      );
    },
    submit() {
      try {
        this.isDataLoading = true;

        // Validation checks
        if (!this.form.customer || !this.form.customer.id) {
          this.$buefy.toast.open({
            message: "Please select a customer!",
            type: "is-danger"
          });
          this.isDataLoading = false;
          return;
        }

        if (!this.validateTotalFileSize(this.form.document)) {
          this.$buefy.toast.open({
            message: "Total file size exceeds 20MB limit!",
            type: "is-danger"
          });
          this.isDataLoading = false;
          return;
        }

        if (!this.form.document || this.form.document.length === 0) {
          this.$buefy.toast.open({
            message: "Please select at least one document!",
            type: "is-danger"
          });
          this.isDataLoading = false;
          return;
        }

        // API calls
        if (this.isEditMode) {
          const editData = {
            ...this.form,
            id: this.editCertificate.id
          };
          this.DataManagementCertificatesService.edit(editData).subscribe(
            () => {
              this.isDataLoading = false;
              this.$buefy.dialog.alert({
                title: "Success",
                message: "Certificate updated successfully.",
                onConfirm: () => {
                  this.$emit("close");
                  this.$emit("refresh");
                }
              });
            },
            e => {
              this.handleApiError(e);
            }
          );
        } else {
          this.DataManagementCertificatesService.create(this.form).subscribe(
            () => {
              this.isDataLoading = false;
              this.$buefy.dialog.alert({
                title: "Success",
                message: "Certificate uploaded successfully.",
                onConfirm: () => {
                  this.$emit("close");
                  this.$emit("refresh");
                }
              });
            },
            e => {
              this.handleApiError(e);
            }
          );
        }
      } catch (error) {
        this.isDataLoading = false;
        console.error("Error in submit:", error);
        this.$buefy.dialog.alert({
          title: "Error",
          message: error?.message || "An unexpected error occurred",
          type: "is-danger",
          ariaRole: "alertdialog",
          ariaModal: true
        });
      }
    },
    downloadCertificateFile() {
      if (this.downloadCertificate && this.editCertificate) {
        this.downloadCertificate(this.editCertificate);
      }
    },
    downloadFileFromTag() {
      if (this.downloadCertificate && this.editCertificate) {
        this.downloadCertificate(this.editCertificate);
      }
    },
    togglePeriod(value) {
      if (value.name == "Transactions") {
        this.showPeriod = true;
      } else {
        this.showPeriod = false;
      }
    },
    deleteFile(index) {
      this.form.document.splice(index, 1);
    },
    validateTotalFileSize(files) {
      if (!files || files.length === 0) {
        this.validationErrors = { ...this.validationErrors, files: null };
        return true;
      }
      const totalSize = files.reduce((sum, file) => {
        // Only count actual File objects (not pre-existing document references)
        return sum + (file.size || 0);
      }, 0);
      if (totalSize > MAX_TOTAL_FILE_SIZE) {
        const totalSizeMB = (totalSize / (1024 * 1024)).toFixed(2);
        this.validationErrors = {
          ...this.validationErrors,
          files: `Total file size (${totalSizeMB}MB) exceeds the maximum allowed size of 20MB.`
        };
        return false;
      }
      this.validationErrors = { ...this.validationErrors, files: null };
      return true;
    },
    handleApiError(reason) {
      try {
        this.isDataLoading = false;
        this.validationErrors = {};
        let errorMessage = "An error occurred";
        const responseData = reason?.response?.data;

        // Check if there are field-specific validation errors
        if (responseData) {
          // Handle field-level errors (e.g., { expiry_date: [...], files: [...] })
          if (
            typeof responseData === "object" &&
            responseData !== null &&
            !Array.isArray(responseData)
          ) {
            // Check for field errors
            let hasFieldErrors = false;
            for (const [key, value] of Object.entries(responseData)) {
              if (Array.isArray(value) && value.length > 0) {
                hasFieldErrors = true;
                this.validationErrors[key] = value[0]; // Get first error message
              }
            }

            if (hasFieldErrors) {
              // Show a general message with field-specific errors displayed below each field
              this.$buefy.toast.open({
                message: "Please fix the validation errors below",
                type: "is-danger"
              });
              const firstFieldError =
                this.validationErrors.files ||
                Object.values(this.validationErrors)[0] ||
                "Please fix the validation errors below";
              this.$buefy.dialog.alert({
                title: "Error",
                message: firstFieldError,
                type: "is-danger",
                ariaRole: "alertdialog",
                ariaModal: true
              });
              return;
            }

            if (responseData["detail"]) {
              errorMessage = responseData["detail"];
            } else if (responseData["error"]) {
              errorMessage = responseData["error"];
            } else if (responseData["message"]) {
              errorMessage = responseData["message"];
            } else if (responseData["name"]) {
              errorMessage = responseData["name"];
            }
          } else if (typeof responseData === "string") {
            errorMessage = responseData;
          }
        }

        // Handle network errors
        if (!reason?.response) {
          errorMessage = reason?.message || "Network error occurred";
        }

        // Handle token expiration
        if (
          reason?.response?.status === 403 &&
          reason?.response?.data?.code === "token_not_valid"
        ) {
          alert("Session expired, please login again");
          if (this.authService) {
            this.authService.logout(this.$keycloak);
          }
          return;
        }

        this.$buefy.dialog.alert({
          title: "Error",
          message: errorMessage,
          type: "is-danger",
          ariaRole: "alertdialog",
          ariaModal: true
        });
      } catch (error) {
        // Fallback error handler
        this.isDataLoading = false;
        this.$buefy.dialog.alert({
          title: "Error",
          message: "An unexpected error occurred",
          type: "is-danger",
          ariaRole: "alertdialog",
          ariaModal: true
        });
      }
    }
  }
};
</script>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style scoped>
.modal-card-body,
.scrollable-modal-body {
  max-height: 70vh;
  overflow-y: auto;
  position: relative;
  padding: 1.5rem;
}
</style>
